// Client layer (TS-2.5.03). The one general reference-data mechanism's own edit form
// (decisions.md D-004: one mechanism for every vocabulary, not per-field tables) - every field
// ReferenceValue carries, editable the same way regardless of which list it belongs to. Adding a
// "team" value through this same form is how D-008's "every new team requires an explicit
// team-group assignment" is satisfied: groupCode is just another field here, never defaulted.
import { useState } from "react";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { COLOR_HEX_PATTERN } from "./lib/colorHex";
import type { ReferenceValueDraft } from "./lib/referenceValueDraft";
import { strings } from "../../strings";

export interface ReferenceValueFormProps {
  readonly idPrefix: string;
  readonly draft: ReferenceValueDraft;
  readonly onChange: (patch: Partial<ReferenceValueDraft>) => void;
}

const F = strings.admin.referenceData;

// The #RRGGBB shape check moved to lib/colorHex.ts when BrandingPanel's per-token hex inputs
// adopted the same hint; the posture (a hint, never a gate - D-241) is unchanged.

export function ReferenceValueForm({ idPrefix, draft, onChange }: ReferenceValueFormProps) {
  const [colorTouched, setColorTouched] = useState(false);
  const colorHexTrimmed = draft.colorHex.trim();
  const showColorHexHint = colorTouched && colorHexTrimmed.length > 0 && !COLOR_HEX_PATTERN.test(colorHexTrimmed);

  return (
    <>
      <FormField id={`${idPrefix}-code`} label={F.code} required>
        <input
          type="text"
          required
          value={draft.code}
          onChange={(event) => onChange({ code: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-code`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-label`} label={F.canonicalLabel} required>
        <input
          type="text"
          required
          value={draft.canonicalLabel}
          onChange={(event) => onChange({ canonicalLabel: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-label`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-short-label`} label={F.shortLabel}>
        <input
          type="text"
          value={draft.shortLabel}
          onChange={(event) => onChange({ shortLabel: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-short-label`)}
        />
      </FormField>
      <FormField
        id={`${idPrefix}-color`}
        label={F.colorHex}
        description={showColorHexHint ? F.colorHexFormatHint : undefined}
      >
        <input
          type="text"
          value={draft.colorHex}
          onChange={(event) => onChange({ colorHex: event.target.value })}
          onBlur={() => setColorTouched(true)}
          {...formFieldControlProps(`${idPrefix}-color`, showColorHexHint ? F.colorHexFormatHint : undefined)}
        />
      </FormField>
      <FormField id={`${idPrefix}-display-order`} label={F.displayOrder}>
        <input
          type="number"
          value={draft.displayOrder}
          onChange={(event) => onChange({ displayOrder: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-display-order`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-sort-tier`} label={F.sortTier}>
        <input
          type="number"
          value={draft.sortTier}
          onChange={(event) => onChange({ sortTier: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-sort-tier`)}
        />
      </FormField>
      <FormField id={`${idPrefix}-group-code`} label={F.groupCode} description={F.addTeamHint}>
        <input
          type="text"
          value={draft.groupCode}
          onChange={(event) => onChange({ groupCode: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-group-code`, F.addTeamHint)}
        />
      </FormField>
      <FormField id={`${idPrefix}-aliases`} label={F.aliases}>
        <input
          type="text"
          value={draft.aliases}
          onChange={(event) => onChange({ aliases: event.target.value })}
          {...formFieldControlProps(`${idPrefix}-aliases`)}
        />
      </FormField>
    </>
  );
}
